import { Message } from "../models/Message";
import { UIMessage } from "../ui/components/UIMessage";
import { Fire } from "./FireClient";
import { My } from "./My";

import { FireClientMsg } from "./FireClientMsg";
export class MessageLoader {
  allMessages: Message[] = [];
  oldMessages: Message[] = [];
  uiMessages: UIMessage[] = [];
  nrNew = 0;
  hasLoaded = false;
  private isLoading = false;
  oldMessagesFromDate: Date | null = null;
  private unsubscribeFn: () => void = () => { };

  constructor(private roomId: string, private onNewMessages: (msgs: UIMessage[], newParentMessages: boolean, visibleMsgs: Message[], invisibleMsg: Message[]) => void) {
  }

  unsubscribe() {
    this.unsubscribeFn();
  }

  async loadOlderMessageAndSubscribeToNew(startDate: Date | null = null, limitCount: number = FireClientMsg.NrMsgsPerLoad): Promise<number> {
    if (this.isLoading) return 0;
    this.isLoading = true;
    try {
      if (!this.hasLoaded) {
        // Initial batch from Firestore (parents only, newest first)
        const initialParents = this.dedupeMessages(await Fire.msg.getMessages(My.user.id, this.roomId, startDate, limitCount));

        // Merge with any pre-seeded messages (e.g., getMessagesAfter result pushed before calling this)
        // Preserve all preloaded parents so "after" messages aren't lost.
        const mergedAll = this.dedupeMessages((this.allMessages ?? []).concat(initialParents));

        this.allMessages = mergedAll.map(m => {
          m.inProgress = false; // snapshot listener will handle updates for new ones; initial ones are static baseline
          return m;
        });
        // Keep oldMessages as the baseline visible set used by subscription handler
        this.oldMessages = this.dedupeMessages((this.oldMessages ?? []).concat(initialParents));
        await this.loadSummaries(this.allMessages);

        if (this.allMessages.length > 0) {
          const newestMessage = await Fire.msg.getLastMessage(My.user.id, this.roomId);
          if (newestMessage) {
            this.oldMessagesFromDate = new Date(newestMessage.created);
          }
          else {
            this.oldMessagesFromDate = null;
          }
        } else {
          this.oldMessagesFromDate = null;
        }

        this.unsubscribeFn();
        this.unsubscribeFn = Fire.msg.subscribeToNewMessages(My.user.id, this.roomId, this.oldMessagesFromDate, async (newMsgs) => {
          let visibleMsgs: Message[] = [];
          let invisibleMsgs: Message[] = [];
          newMsgs.forEach(msg => {
            if (msg.parentMessageId) {
              let parent = this.allMessages.find(m => m.id == msg.parentMessageId);
              if (parent) {
                visibleMsgs.push(msg);
              } else {
                invisibleMsgs.push(msg);
              }
            } else {
              visibleMsgs.push(msg);
            }
            // Update summary for parents even if invisible
            if (msg.parentMessageId) {
              let parent = this.allMessages.find(m => m.id == msg.parentMessageId);
              if (parent) {
                // 🔧 리액션(emoticon)은 replyCount에 포함하지 않음
                if (!msg.emoticon) {
                  parent.replyCount = (parent.replyCount ?? 0) + 1;
                  parent.lastReply = msg;
                  if (msg.user.id !== My.user.id) {
                    parent.unreadReplyCount = (parent.unreadReplyCount ?? 0) + 1;
                  }
                }
              }
            }
          });
          // console.log("newMsgs2", visibleMsgs, invisibleMsgs);
          let prevMessageLength = this.uiMessages.length;
          if (visibleMsgs.length > 0) {
            const merged = visibleMsgs.concat(this.oldMessages);
            this.oldMessages = this.dedupeMessages(merged);
            // Merge into allMessages without losing any preloaded messages
            this.allMessages = this.dedupeMessages(this.allMessages.concat(visibleMsgs));
          }
          let _messages = this.getUIMessages();
          let newParentMessages = _messages.length > prevMessageLength;
          this.onNewMessages(_messages, newParentMessages, visibleMsgs, invisibleMsgs);
        });

        this.hasLoaded = true;
        return this.oldMessages.filter(m => !m.parentMessageId).length;
      } else if (this.allMessages.length > 0) {
        let lastMessage = this.allMessages[0];
        console.log(`📍 [LASTMSG] id: ${lastMessage.id.substring(0, 8)}..., created: ${lastMessage.created}, parentMessageId: ${lastMessage.parentMessageId}, text: ${lastMessage.text?.substring(0, 30) || lastMessage.autoMessage}`);
        let moreMessages: Message[] = [];
        if (startDate) {
          moreMessages = await Fire.msg.getMessagesBetweenCreated(My.user.id, this.roomId, new Date(lastMessage.created), startDate, limitCount);
        } else {
          moreMessages = await Fire.msg.getMessages(My.user.id, this.roomId, new Date(lastMessage.created), limitCount);
        }
        await this.loadSummaries(moreMessages);
        const beforeDedupeCount = this.allMessages.length + moreMessages.length;
        this.allMessages = this.dedupeMessages(this.allMessages.concat(moreMessages));
        const afterDedupeCount = this.allMessages.length;
        console.log(`🔍 [DEDUPE] moreMessages: ${moreMessages.length}, beforeDedupe: ${beforeDedupeCount}, afterDedupe: ${afterDedupeCount}, removed: ${beforeDedupeCount - afterDedupeCount}`);
        let allOlder = this.dedupeMessages(this.oldMessages.concat(moreMessages));
        this.oldMessages = allOlder; // Otherwise the subscription handler will remove moreMessages from allMessages.
        const parentCount = moreMessages.filter(m => !m.parentMessageId).length;
        console.log(`🔍 [LOADMORE] moreMessages parents: ${parentCount}, returning: ${parentCount}`);
        return parentCount;
      }
      else
        return 0;
    } finally {
      this.isLoading = false;
    }
  }

  getUIMessages(): UIMessage[] {
    // Oldest first, newest last in list.
    const dedupedMessages = this.dedupeMessages(this.allMessages);
    const allMsgs = dedupedMessages.sort((a, b) => a.created > b.created ? 1 : -1);
    // console.log("allMsgs", allMsgs);
    let _parents: UIMessage[] = [];
    let _parentList = allMsgs.filter(m => !m.parentMessageId);
    let childMap = new Map<string, Message[]>(); // parentId / message
    let lastDate: string | null = null;
    const addDateIfNewDay = (uimsg: UIMessage) => {
      const msgDate = new Date(uimsg.msg.created).toDateString();
      if (lastDate !== msgDate) {
        uimsg.isNewDay = true;
        lastDate = msgDate;
      }
    };

    allMsgs.filter(m => !!m.parentMessageId).forEach(m => {
      if (m.emoticon && m.replyToMessageId) {
        if (!childMap.has(m.replyToMessageId)) {
          childMap.set(m.replyToMessageId, [])
        }
        childMap.get(m.replyToMessageId)!.push(m);
      } else {
        if (!childMap.has(m.parentMessageId!)) {
          childMap.set(m.parentMessageId!, [])
        }
        childMap.get(m.parentMessageId!)!.push(m);
      }
    });

    _parentList.forEach(msg => {
      let kids: UIMessage[] = childMap.get(msg.id)?.map(c => {
        let grandKids = childMap.get(c.id)?.map(gc => new UIMessage(gc, [])) ?? [];
        let kid = new UIMessage(c, grandKids);
        return kid;
      }) ?? [];

      let parent = new UIMessage(msg, kids);
      parent.replyCount = msg.replyCount ?? kids.length;
      parent.lastReply = msg.lastReply ?? (kids.length > 0 ? kids[kids.length - 1].msg : null);
      parent.repliesLoaded = msg.repliesLoaded || false; // Important: direct sync from raw message
      parent.unreadReplyCount = msg.unreadReplyCount || 0;
      _parents.push(parent);
    });
    _parents.forEach(uimsg => {
      uimsg.isNewDay = false;
      addDateIfNewDay(uimsg);
    });

    this.allMessages = allMsgs;
    this.uiMessages = _parents;
    return this.uiMessages;
  }

  private dedupeMessages(messages: Message[]): Message[] {
    const map = new Map<string, Message>();

    messages.forEach((msg) => {
      const existing = map.get(msg.id);
      if (!existing) {
        map.set(msg.id, msg);
        return;
      }

      const existingCreated = existing.created instanceof Date ? existing.created : new Date(existing.created);
      const currentCreated = msg.created instanceof Date ? msg.created : new Date(msg.created);

      if (currentCreated >= existingCreated) {
        // 🔧 FIX: 새 메시지를 선택할 때 기존 메시지의 reply 관련 속성 보존
        // Firebase에서 새로 가져온 메시지에는 이 속성들이 없을 수 있음
        const merged = { ...msg };
        if (existing.replyCount !== undefined && msg.replyCount === undefined) {
          merged.replyCount = existing.replyCount;
        }
        if (existing.lastReply !== undefined && msg.lastReply === undefined) {
          merged.lastReply = existing.lastReply;
        }
        if (existing.repliesLoaded !== undefined && msg.repliesLoaded === undefined) {
          merged.repliesLoaded = existing.repliesLoaded;
        }
        if (existing.unreadReplyCount !== undefined && msg.unreadReplyCount === undefined) {
          merged.unreadReplyCount = existing.unreadReplyCount;
        }
        map.set(msg.id, merged);
      }
    });

    return Array.from(map.values());
  }


  static getNextMessage(current: UIMessage | null, parents: UIMessage[]): UIMessage | null {
    let next: UIMessage | null = null;
    if (current) {
      const hasChildren = current.children && current.children.length > 0;
      const isChild = Boolean(current.msg.parentMessageId);

      if (hasChildren && !isChild) {
        next = current.children[0];
        // Go to first child
      } else if (isChild) {
        // Go to next sibling
        let parent = parents.find(m => m.msg.id == current.msg.parentMessageId);
        if (parent) {
          let index = parent.children.findIndex(c => c.msg.id == current.msg.id);
          if (index + 1 < parent.children.length) {
            next = parent.children[index + 1];
          }
        }
      }

      if (next == null) {
        // Go to next parent
        let parentId = current.msg.parentMessageId ?? current.msg.id;
        let index = parents.findIndex(m => m.msg.id == parentId);
        if (index > -1 && index + 1 < parents.length) {
          next = parents[index + 1];
        }
      }
    }

    if (next?.msg.emoticon) {
      return this.getNextMessage(next, parents);
    } else {
      return next;
    }
  }

  async loadSummaries(parents: Message[]) {
    // Only load summaries for messages that haven't been loaded yet and have no children locally
    const needsSummary = parents.filter(p => !p.parentMessageId && !p.repliesLoaded);
    const allReactions: Message[] = [];

    const promises = needsSummary.map(async (p) => {
      const summary = await Fire.msg.getChildSummary(My.user.id, p.id);
      // 🔧 replyCount에서 reactions 수를 빼서 실제 댓글 수만 표시
      const reactionsCount = summary.reactions?.length || 0;
      p.replyCount = summary.count - reactionsCount;
      p.lastReply = summary.lastMessage;
      // 🔧 reactions 데이터 수집 - 스레드 열기 전에도 표시하기 위함
      if (summary.reactions && summary.reactions.length > 0) {
        allReactions.push(...summary.reactions);
      }
    });
    await Promise.all(promises);

    // 🔧 reactions를 allMessages에 추가하여 UIMessage.reactions()에서 사용 가능하도록
    if (allReactions.length > 0) {
      this.allMessages = this.dedupeMessages(this.allMessages.concat(allReactions));
    }
  }

  ingestLoadedChildren(parentId: string, children: Message[]) {
    // 1. Merge and dedupe first to get the final message list
    this.allMessages = this.dedupeMessages(this.allMessages.concat(children));
    this.oldMessages = this.dedupeMessages(this.oldMessages.concat(children));

    // 2. Now find the parent in the FINAL list and set the flags
    const parent = this.allMessages.find(m => m.id === parentId);
    if (parent) {
      parent.repliesLoaded = true;
      parent.replyCount = children.length;
    }

    // Refresh UI - Pass empty arrays for visible/invisible updates to prevent "New Message" toasts
    let _messages = this.getUIMessages();
    this.onNewMessages(_messages, false, [], []);
  }

  async ingestMessages(messages: Message[]) {
    await this.loadSummaries(messages);
    this.allMessages = this.dedupeMessages(this.allMessages.concat(messages));
    this.oldMessages = this.dedupeMessages(this.oldMessages.concat(messages));

    let _uiMessages = this.getUIMessages();
    // Do not pass messages to visible list and set newParentMessages to FALSE 
    // to avoid triggering bottom scroll during background ingest
    this.onNewMessages(_uiMessages, false, [], []);
  }
}
